import { CustomAccordion, Button } from 'components';
import { getNextFromDate } from 'helpers/timeWindow/getNextFromDate';
import { getToDates } from 'helpers/timeWindow/getToDates';
import { useTWScreenParams } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'react-bootstrap-icons';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ContainerOutline, Header } from './TimeWindowStyles';
import { ContentContainer } from 'components/TableTabbed/TableTabbedStyles';
import { BenchmarkTypes } from 'types/Benchmark';
import { v4 as uuidV4 } from 'uuid';
import { transpileModule } from 'typescript';

export type TimeWindowFormProps = {
  index: number;
  refreshDates?: Function;
  onClone?: Function;
  onRemove?: Function;
};

type Props = {
  TimeWindowComponent: React.FC<TimeWindowFormProps>;
  defaultValues: Object;
  benchmarkType: BenchmarkTypes;
  timeWindowsFilled?: boolean;
  HeaderComponent?: any;
  benchmarkId?: string;
};

type FormValues = {
  timeWindows: Array<any>;
};

export type ToggleTimeWindowsProps = {
  index?: string;
  forceOpen?: boolean;
  openAll?: boolean;
};

export const TimeWindows = ({
  HeaderComponent,
  TimeWindowComponent,
  defaultValues,
  timeWindowsFilled = true,
  benchmarkId,
}: Props) => {
  const [newTimeWindowAdded, setNewTimeWindowAdded] = useState(false);
  const { addTimeWindow } = useTWScreenParams(benchmarkId);
  const withToggles = useMemo(
    () => Boolean(HeaderComponent),
    [HeaderComponent]
  );
  const { getValues, trigger, setValue, watch, clearErrors, control } =
    useFormContext();
  const calculationFrequencyId = watch('calculationFrequencyId');
  const { prepend, replace, fields } = useFieldArray<
    FormValues,
    'timeWindows',
    'id'
  >({
    name: 'timeWindows',
    // @ts-ignore
    control,
  });
  const [timeWindowHeaders, setTimeWindowHeaders] = useState<null | Record<
    string,
    boolean
  >>(null);
  const handleRemove = (index: number) => {
    const timeWindows = watch('timeWindows');
    const updatedTimeWindows = getToDates(
      timeWindows.filter((_: any, i: number) => i !== index)
    );
    setValue('timeWindows', updatedTimeWindows, {
      shouldValidate: true,
      shouldDirty: true,
    });
    clearErrors();
    trigger();
  };

  const handleAdd = (skipValidation?: boolean) => {
    const newTimeWindowCB = () => {
      const newTimeWindow = getNextFromDate({
        timeWindow: {
          ...defaultValues,
          isNew: transpileModule,
          toggleId: uuidV4(),
        },
        calculationFrequencyId,
        fields,
      });

      prepend(newTimeWindow);
      if (fields.length) {
        setValue(`timeWindows[1].toDate`, newTimeWindow.fromDate);
      }
      trigger('timeWindows');
    };

    if (skipValidation) {
      newTimeWindowCB();
    } else {
      trigger(undefined, { shouldFocus: true }).then((res) => {
        if (res) {
          newTimeWindowCB();
        }
      });
    }
  };

  const handleDatesRefresh = () => {
    const timeWindows = getToDates(getValues(`timeWindows`));
    replace(timeWindows);
    trigger(undefined);
  };

  const handleClone = (timeWindow: any) => {
    trigger(undefined, { shouldFocus: true }).then((res) => {
      if (res) {
        const preclonedTimeWindow = {
          ...timeWindow,
          toDate: '',
        };
        const clonedTimeWindow: any = getNextFromDate({
          timeWindow: {
            ...preclonedTimeWindow,
            isNew: true,
            toggleId: uuidV4(),
          },
          calculationFrequencyId,
          fields,
        });
        delete clonedTimeWindow.id;
        prepend(clonedTimeWindow);
        setValue(`timeWindows[${1}].toDate`, clonedTimeWindow.fromDate);
      }
    });
  };

  const handleToggleHeaders = ({
    index,
    openAll,
    forceOpen,
  }: ToggleTimeWindowsProps) => {
    if (!timeWindowHeaders) return;
    const newState: Record<string, any> = {};

    if (!index) {
      for (const k in timeWindowHeaders) {
        newState[k] = openAll;
      }
      setTimeWindowHeaders(newState);
      return;
    }

    const updated = { ...timeWindowHeaders };
    updated[index] = forceOpen ? true : !updated[index];
    setTimeWindowHeaders(updated);
  };

  const setTimeWindowHeadersCB = (expandFirst = false) => {
    setTimeWindowHeaders(
      fields.reduce((a: any, c: any, i: number) => {
        if (i === 0 && expandFirst) {
          a[c.toggleId] = true;
        } else {
          a[c.toggleId] = timeWindowHeaders
            ? timeWindowHeaders[c.toggleId]
            : false;
        }

        return a;
      }, {})
    );
  };

  useEffect(() => {
    // to setup expand/collapse accordions ('fields' isn't loaded at the first render)
    if (fields.length > 0) {
      if (addTimeWindow && !newTimeWindowAdded) {
        handleAdd(true);
        setNewTimeWindowAdded(true);
      } else {
        setTimeWindowHeadersCB(true);
      }
    } else {
      handleAdd(true);
      setNewTimeWindowAdded(true);
    }
  }, [fields, addTimeWindow]);

  const timeWindowsComponents = useMemo(() => {
    if (!timeWindowHeaders) return;

    if (!withToggles) {
      return fields.map((field: any, index: number) => {
        const key = `${index}-${field.id}`;

        return (
          <ContainerOutline isNew={field.isNew} key={key}>
            <TimeWindowComponent
              index={index}
              refreshDates={handleDatesRefresh}
              onClone={handleClone}
              onRemove={handleRemove}
            />
          </ContainerOutline>
        );
      });
    }

    return fields.map((field: any, index: number) => {
      const key = `${index}-${field.id}`;
      return (
        <CustomAccordion
          isOpened={timeWindowHeaders[field.toggleId]}
          onClick={handleToggleHeaders}
          index={field.toggleId}
          key={key}
          isNew={field.isNew}
          headerElements={
            <HeaderComponent
              isOpened={timeWindowHeaders[field.toggleId]}
              handleToggleHeaders={handleToggleHeaders}
              index={index}
              timeWindow={field}
              onClone={handleClone}
              onRemove={handleRemove}
              refreshDates={handleDatesRefresh}
            />
          }
          size='14px'
          color='#0076C0'
        >
          <TimeWindowComponent key={key} index={index} />
        </CustomAccordion>
      );
    });
  }, [fields, timeWindowHeaders]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          margin: '20px',
          gap: '8px',
        }}
      >
        {!benchmarkId ? <Header>Time Windows</Header> : null}
        {withToggles ? (
          <>
            <Button
              variant='secondary'
              onClick={() => {
                handleToggleHeaders({ openAll: true });
              }}
            >
              Expand All
            </Button>
            <Button
              variant='secondary'
              onClick={() => {
                handleToggleHeaders({ openAll: false });
              }}
            >
              Collapse All
            </Button>
          </>
        ) : null}
        <Button
          disabled={!timeWindowsFilled}
          data-testid='add-new-time-window-button'
          variant='primary'
          onClick={() => handleAdd()}
        >
          <Plus size='20px' />
          Time Window
        </Button>
      </div>
      <ContentContainer>{timeWindowsComponents}</ContentContainer>
    </>
  );
};
